import { ethers } from "ethers";
import type { PrivateFeePolicy } from "../base-app/hooks/usePrivateFeePolicy";
import type {
  FlatJoinSplitV2WithdrawParams,
  FlatJoinSplitV2OperationResult,
} from "./operationTypes";
import {
  bridgeDeploymentFor,
  decodePrivateBridge,
  PRIVATE_BRIDGE_INTERFACE,
  PRIVATE_CCIP_BRIDGE_INTERFACE,
  isQualifiedBridgeAsset,
  isQualifiedCcipBridgeAsset,
  bridgeRouteValue,
  type PrivateBridgeProvider,
} from "./privateBridgeConfig";
import { preparePrivateFeeSpend } from "./privateFees";
import { withFlatJoinSplitV2WalletLock } from "./walletLock";
import { EVM_CONFIG } from "../config";
import { sendSimulated } from "./settlement";
import { getBaseReadProvider } from "../utils/base-read-provider";
import { requestPrivateFees } from "../utils/relayer-client";

export interface PrivateBridgeRoute {
  id: string;
  provider: PrivateBridgeProvider;
  token: string;
  sourceChainId: number;
  destinationLabel: string;
  assetLabel: string;
  decimals: number;
  tokenId?: string;
  destinationChain?: "solana";
  destinationNetwork?: "devnet";
  destinationMint?: string;
  destinationChainSelector?: string;
  destinationChainId?: number;
  destinationToken?: string;
}
export interface PrivateBridgeQuote {
  chainId: number;
  pool: string;
  coordinator: string;
  provider?: PrivateBridgeProvider;
  route: PrivateBridgeRoute;
  amount: string;
  received: string;
  destinationAddress: string;
  protocolFee: string;
  partnerFee: string;
  gasFee: string;
  deliveryWei: string;
  quotedAt: number;
  relayerAddress: string;
  policy: PrivateFeePolicy;
}
export const bridgeProviderName = (provider: string) =>
  provider === "ccip"
    ? "Chainlink CCIP"
    : provider === "axelar"
      ? "Axelar"
      : provider;
export async function executePrivateFeeBridge(
  params: FlatJoinSplitV2WithdrawParams & { bridgeQuote: PrivateBridgeQuote },
): Promise<FlatJoinSplitV2OperationResult & { bridgeId?: string }> {
  try {
    return await withFlatJoinSplitV2WalletLock({
      chainId: EVM_CONFIG.CHAIN_ID,
      poolAddress: EVM_CONFIG.CONTRACTS.SHIELDED_POOL_JOINSPLIT,
      accountAddress: await params.signer.getAddress(),
      signal: params.signal,
      run: async () => {
        const q = params.bridgeQuote,
          account = await params.signer.getAddress(),
          provider = q.route.provider;
        const deployment =
          provider === "axelar" || provider === "ccip"
            ? bridgeDeploymentFor(provider)
            : null;
        const qualified =
          provider === "ccip"
            ? isQualifiedCcipBridgeAsset(q.route)
            : isQualifiedBridgeAsset(q.route);
        const destinationValid =
          provider === "ccip"
            ? ethers.isAddress(q.destinationAddress) &&
              q.destinationAddress !== ethers.ZeroAddress
            : ethers.isHexString(q.destinationAddress, 32);
        if (
          !deployment ||
          !qualified ||
          (q.provider ?? provider) !== provider ||
          q.coordinator !== deployment.coordinator ||
          q.chainId !== 84532 ||
          q.route.sourceChainId !== 84532 ||
          q.route.token !== ethers.getAddress(params.tokenAddress) ||
          BigInt(q.amount) !== params.amount ||
          Date.now() - q.quotedAt > 120000 ||
          q.quotedAt > Date.now() + 5000 ||
          !destinationValid
        )
          throw Error("Refresh the bridge quote before continuing");
        const bridge = {
          provider,
          routeId: q.route.id,
          destinationAddress: q.destinationAddress.toLowerCase(),
          refundRecipient: account,
          deadline: Math.floor(Date.now() / 1000) + 900,
          salt: ethers.hexlify(ethers.randomBytes(32)),
        };
        const prepared = await preparePrivateFeeSpend(params, 1, {
          pool: q.pool,
          policy: q.policy,
          bridge,
          bridgeQuote: q,
        });
        const authorized = prepared.authorized.bridge,
          tx = authorized?.transaction,
          decoded = tx && decodePrivateBridge(tx.data);
        if (
          !tx ||
          !decoded ||
          decoded.provider !== provider ||
          decoded.recovery ||
          tx.to !== deployment.coordinator ||
          tx.chainId !== 84532 ||
          BigInt(tx.value) !== bridgeRouteValue(provider, decoded.route) ||
          !ethers.isAddress(authorized!.relayerAddress)
        )
          throw Error("Bridge authorization did not match");
        const iface =
          provider === "ccip"
            ? PRIVATE_CCIP_BRIDGE_INTERFACE
            : PRIVATE_BRIDGE_INTERFACE;
        const expected = iface.encodeFunctionData("execute", [
          prepared.quote.request!.bridgeRoute,
          prepared.leg,
          iface.decodeFunctionData("execute", tx.data).permit,
          ...(prepared.recovery ? [prepared.recovery.records] : []),
        ]);
        if (expected.toLowerCase() !== tx.data.toLowerCase())
          throw Error("Bridge authorization changed the transaction");
        const settled = await sendSimulated(
          params.signer,
          prepared.provider,
          {
            to: tx.to,
            data: tx.data as `0x${string}`,
            value: BigInt(tx.value),
            chainId: prepared.chainId,
          },
          prepared.pool,
          prepared.ownedOrSent,
          params.onLog,
          prepared.accountAddress,
          (signed) => prepared.journal(signed),
          true,
          params.signal,
          undefined,
          getBaseReadProvider(),
          () =>
            requestPrivateFees("submit", prepared.authorizationInput, {
              pool: prepared.pool,
              signal: params.signal,
            }),
          authorized!.relayerAddress,
        );
        return {
          success: true,
          txHash: settled.txHash,
          bridgeId: prepared.quote.id,
          journalId: settled.journalId,
          spentNotes: prepared.selected.map((n) => n.note),
          outputNotes: settled.outputs,
        };
      },
    });
  } catch (error: any) {
    return {
      success: false,
      spentNotes: [],
      outputNotes: [],
      error: error?.message || "Bridge failed",
      txHash: error?.txHash,
      journalId: error?.journalId,
    };
  }
}
